import { getDistance } from 'geolib';

interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Calculate distance between two coordinates and format it as a string
 * @param from - Starting coordinates (current location)
 * @param to - Destination coordinates (business location)
 * @returns Formatted distance string (e.g., "2.5 km" or "500 m")
 */
export const calculateDistance = (
  from: Coordinates | null | undefined,
  to: Coordinates | null | undefined
): string | undefined => {
  if (!from || !to) {
    return undefined;
  }

  if (!from.latitude || !from.longitude || !to.latitude || !to.longitude) {
    return undefined;
  }

  try {
    // getDistance returns distance in meters
    const distanceInMeters = getDistance(
      { latitude: from.latitude, longitude: from.longitude },
      { latitude: to.latitude, longitude: to.longitude }
    );

    // Format distance
    if (distanceInMeters < 1000) {
      // Less than 1km, show in meters
      return `${distanceInMeters} m`;
    } else {
      // 1km or more, show in kilometers with 1 decimal place
      const distanceInKm = (distanceInMeters / 1000).toFixed(1);
      return `${distanceInKm} km`;
    }
  } catch (error) {
    console.error('Error calculating distance:', error);
    return undefined;
  }
};

/**
 * Calculate raw distance in kilometers between two coordinates
 * @param from - Starting coordinates
 * @param to - Destination coordinates
 * @returns Distance in kilometers or null if coordinates are invalid
 */
export const getDistanceInKm = (
  from: Coordinates | null | undefined,
  to: Coordinates | null | undefined
): number | null => {
  if (!from || !to) {
    return null;
  }

  if (!from.latitude || !from.longitude || !to.latitude || !to.longitude) {
    return null;
  }

  try {
    const distanceInMeters = getDistance(
      { latitude: from.latitude, longitude: from.longitude },
      { latitude: to.latitude, longitude: to.longitude }
    );

    return distanceInMeters / 1000;
  } catch (error) {
    console.error('Error calculating distance:', error);
    return null;
  }
};
